(() => {
  class MarameEditorialStory extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.renderedChapterIndex = -1;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.chapters = Array.from(this.querySelectorAll('[data-story-chapter]'));
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.setupSnap(signal);
      this.nav = this.querySelector('.marame-editorial-story__nav');
      if (!this.chapters.length || !this.nav) return;
      this.picker = this.querySelector('[data-story-picker]');
      this.previous = this.querySelector('[data-story-previous]');
      this.next = this.querySelector('[data-story-next]');
      this.querySelector('[data-story-total]').textContent = String(this.chapters.length).padStart(2, '0');
      this.nav.addEventListener('click', event => {
        const link = event.target.closest('a');
        if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        const chapter = this.chapters.find(item => `#${item.id}` === link.getAttribute('href'));
        if (!chapter) return;
        event.preventDefault();
        this.picker.open = false;
        this.enableSnap();
        chapter.scrollIntoView({behavior: this.reduced.matches ? 'auto' : 'smooth', block: 'start'});
        chapter.focus({preventScroll: true});
      }, {signal});
      this.nav.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.picker.open) {
          this.picker.open = false;
          this.picker.querySelector('summary').focus();
        }
      }, {signal});
      document.addEventListener('click', event => {
        if (!this.nav.contains(event.target)) this.picker.open = false;
      }, {signal});
      this.addEventListener('shopify:block:select', event => {
        const chapter = event.target.closest('[data-story-chapter]');
        if (this.chapters.includes(chapter)) chapter.scrollIntoView({behavior: 'auto', block: 'start'});
      }, {signal});
      const schedule = () => {
        if (signal.aborted || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          if (signal.aborted) return;
          this.frame = null;
          this.updateChapter();
        });
      };
      window.addEventListener('scroll', schedule, {passive: true, signal});
      window.addEventListener('resize', schedule, {passive: true, signal});
      this.updateChapter();
    }
    resetSnap() {
      this.snapGeneration = (this.snapGeneration || 0) + 1;
      this.snapReady = false;
      this.removeAttribute('data-story-snap-active');
      if (this.snapFrame) cancelAnimationFrame(this.snapFrame);
      this.snapFrame = null;
    }
    setupSnap(signal) {
      this.resetSnap();
      if (!this.hasAttribute('data-story-snap') || !this.chapters.length) return;
      const settle = () => {
        const generation = this.snapGeneration;
        Promise.resolve(document.fonts?.ready).catch(() => {}).then(() => {
          if (signal.aborted || generation !== this.snapGeneration) return;
          this.snapFrame = requestAnimationFrame(() => {
            if (signal.aborted || generation !== this.snapGeneration) return;
            this.snapFrame = requestAnimationFrame(() => {
              this.snapFrame = null;
              if (!signal.aborted && generation === this.snapGeneration) this.snapReady = true;
            });
          });
        });
      };
      const prepare = () => {
        this.resetSnap();
        if (document.readyState === 'complete') settle();
        else window.addEventListener('load', settle, {once: true, signal});
      };
      const intent = event => {
        if (!event.isTrusted || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
        if (event.target.closest?.('dialog, input, textarea, select, button, a, summary, [contenteditable=""], [contenteditable="true"], [data-story-picker][open]')) return;
        if (event.type === 'wheel' && !event.deltaY) return;
        if (event.type === 'keydown' && !['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) return;
        this.enableSnap();
      };
      window.addEventListener('wheel', intent, {passive: true, signal});
      window.addEventListener('touchmove', intent, {passive: true, signal});
      window.addEventListener('keydown', intent, {signal});
      window.addEventListener('pagehide', () => this.resetSnap(), {signal});
      window.addEventListener('pageshow', prepare, {signal});
      prepare();
    }
    enableSnap() {
      if (this.snapReady && !this.reduced.matches) this.setAttribute('data-story-snap-active', '');
    }
    updateChapter() {
      const top = this.nav.getBoundingClientRect().bottom + 40;
      let index = 0;
      this.chapters.forEach((chapter, i) => { if (chapter.getBoundingClientRect().top <= top) index = i; });
      if (this.renderedChapterIndex === index) return;
      const chapter = this.chapters[index];
      this.querySelector('[data-story-current]').textContent = String(index + 1).padStart(2, '0');
      this.querySelector('[data-story-name]').textContent = chapter.dataset.storyTitle;
      this.querySelector('[data-story-progress]').style.width = `${((index + 1) / this.chapters.length) * 100}%`;
      this.querySelectorAll('[data-story-jump]').forEach(link => {
        if (link.getAttribute('href') === `#${chapter.id}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      for (const [link, target] of [[this.previous, this.chapters[index - 1]], [this.next, this.chapters[index + 1]]]) {
        link.hidden = !target;
        if (target) { link.setAttribute('href', `#${target.id}`); link.setAttribute('aria-label', target.dataset.storyTitle); }
        else { link.removeAttribute('href'); link.removeAttribute('aria-label'); }
      }
      this.renderedChapterIndex = index;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resetSnap();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }
  if (!customElements.get('marame-editorial-story')) customElements.define('marame-editorial-story', MarameEditorialStory);
})();
